import { submitContactFormMutation } from '@/api/contacts/contactMutations';
import { currentUserQuery } from '@/api/user/userQueries';
import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import { Input } from '@/components/common/Input';
import { Section } from '@/components/common/Section';
import { Spinner } from '@/components/common/Spinner';
import { Textarea } from '@/components/common/Textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { z } from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

const contactSchema = z.object({
  email: z.email('Invalid email address'),
  subject: z
    .string()
    .min(5, 'Subject must be at least 5 characters')
    .max(1000, 'Subject must be less than 1000 characters'),
  description: z
    .string()
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message must be less than 5000 characters'),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export const ContactsPage = () => {
  const { data: user } = useQuery({ ...currentUserQuery, retry: false });
  const contactMutation = useMutation(submitContactFormMutation);

  const formik = useFormik<ContactFormValues>({
    initialValues: {
      email: user?.data.email || '',
      subject: '',
      description: '',
    },
    enableReinitialize: true,
    validate: toFormikValidate(contactSchema),
    onSubmit: async (values, { resetForm }) => {
      await contactMutation.mutateAsync(values);
      resetForm();
    },
  });

  return (
    <main>
      <Section>
        <div className="flex max-w-175 flex-col gap-5">
          <Heading as="h1" size="h1" className="text-secondary text-left">
            Contact Us
          </Heading>

          <p>
            Get in touch by filling out this form. Our support team is available
            8am-10pm BST weekdays and 10am-8pm BST on weekends.
          </p>

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
            <Input
              id="email"
              label="Your email address"
              type="email"
              {...formik.getFieldProps('email')}
              error={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : undefined
              }
              touched={formik.touched.email}
            />

            <Input
              id="subject"
              label="Subject"
              {...formik.getFieldProps('subject')}
              error={
                formik.touched.subject && formik.errors.subject
                  ? formik.errors.subject
                  : undefined
              }
              touched={formik.touched.subject}
            />

            <Textarea
              id="description"
              label="Message"
              {...formik.getFieldProps('description')}
              error={
                formik.touched.description && formik.errors.description
                  ? formik.errors.description
                  : undefined
              }
              touched={formik.touched.description}
              rows={4}
            />

            <p>
              Please enter the details of your request. A member of our support
              team will respond in 24 hours.
            </p>

            {contactMutation.isError && (
              <p className="text-destructive">
                {contactMutation.error.message}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-fit"
              disabled={
                !formik.isValid || !formik.dirty || contactMutation.isPending
              }
            >
              Submit {contactMutation.isPending ? <Spinner /> : null}
            </Button>
          </form>
        </div>
      </Section>
    </main>
  );
};
